import { useState, useEffect } from 'react';
import { getMealCounts, getTeams } from '../../utils/api';

export default function AdminMeals({ showToast }) {
  const [counts, setCounts] = useState(null);
  const [excludedTeams, setExcludedTeams] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([getMealCounts(), getTeams()])
      .then(([mealData, teamData]) => {
        setCounts(mealData || {});
        const excluded = (teamData || [])
          .filter(t => t.excludeMeals === true || t.excludeMeals === 'true' || t.excludeMeals === 'TRUE')
          .map(t => t.name);
        setExcludedTeams(excluded);
      })
      .catch(err => {
        console.error('Failed to load meal counts:', err);
        showToast && showToast('Error loading meal counts.');
        setCounts({});
      })
      .finally(() => setLoading(false));
  }, []);

  // Parse the keyed object into rows grouped by day
  const dayOrder = [
    'Saturday', 'Sunday', 'Monday', 'Tuesday', 'Wednesday',
    'Thursday', 'Friday', 'Saturday', 'Sunday',
  ];
  const MEAL_TYPES = ['Breakfast', 'Lunch', 'Dinner'];

  // Collect unique days from the data, preserving conference order
  const rows = [];
  if (counts) {
    const seenDays = {};
    Object.values(counts).forEach(item => {
      const key = item.day + '_' + (item.date || '');
      if (!seenDays[key]) {
        seenDays[key] = { day: item.day, date: item.date || '' };
      }
    });
    // Sort by date
    Object.values(seenDays)
      .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0))
      .forEach(({ day, date }) => {
        const rowData = { day, date };
        let dayTotal = 0;
        MEAL_TYPES.forEach(mt => {
          // date-keyed (unique — repeated weekdays used to collide); weekday
          // fallback keeps a pre-redeploy backend readable.
          const entry = counts[date + '_' + mt] || counts[day + '_' + mt];
          const c = entry ? (entry.count || 0) : null;
          rowData[mt] = c;
          if (c !== null) dayTotal += c;
        });
        rowData.total = dayTotal;
        rows.push(rowData);
      });
  }

  const hasAnyData = rows.length > 0;
  const grandTotal = rows.reduce((sum, r) => sum + r.total, 0);

  return (
    <div className="page-content">
      <div className="page-title-row">
        <div>
          <div className="page-title">Meal Counts</div>
          <div className="page-sub">
            Auto-calculated from block roster assignments · read-only
            {excludedTeams.length > 0 && (
              <span style={{ marginLeft: 8, color: 'var(--muted)' }}>
                · excluding: {excludedTeams.join(', ')}
              </span>
            )}
          </div>
        </div>
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted)' }}>Loading…</div>
      ) : !hasAnyData ? (
        <div className="panel" style={{ padding: '40px 24px', textAlign: 'center' }}>
          <div style={{ fontSize: 15, fontFamily: 'var(--font-h)', fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>
            No meal windows configured yet
          </div>
          <div style={{ fontSize: 13, fontFamily: 'var(--font-b)', fontWeight: 300, color: 'var(--muted)' }}>
            Meal counts will appear here once meals are added to the Master Schedule and people are rostered to blocks.
          </div>
        </div>
      ) : (
        <>
          <div className="stat-grid" style={{ gridTemplateColumns: 'repeat(3,1fr)' }}>
            <div className="stat-card">
              <div className="stat-label">Total Meal Servings</div>
              <div className="stat-value">{grandTotal.toLocaleString()}</div>
              <div className="stat-sub">Across all days and meal types</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Conference Days</div>
              <div className="stat-value">{rows.length}</div>
              <div className="stat-sub">Days with meal windows</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Meal Windows</div>
              <div className="stat-value">{Object.keys(counts).length}</div>
              <div className="stat-sub">Total scheduled meal slots</div>
            </div>
          </div>

          <div className="panel">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Day</th>
                  <th>Date</th>
                  <th>Breakfast</th>
                  <th>Lunch</th>
                  <th>Dinner</th>
                  <th>Day Total</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(row => (
                  <tr key={row.day + row.date}>
                    <td><span className="td-name">{row.day}</span></td>
                    <td style={{ color: 'var(--muted)', fontSize: 13 }}>{row.date}</td>
                    {MEAL_TYPES.map(mt => (
                      <td key={mt} style={{ textAlign: 'center', fontFamily: 'var(--font-h)', fontWeight: row[mt] > 0 ? 700 : 400, color: row[mt] === null ? 'var(--muted)' : 'var(--text)' }}>
                        {row[mt] === null ? '—' : row[mt].toLocaleString()}
                      </td>
                    ))}
                    <td style={{ textAlign: 'center', fontFamily: 'var(--font-h)', fontWeight: 700, color: 'var(--accent)' }}>
                      {row.total.toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ padding: '12px 20px', borderTop: '1px solid var(--border)', fontFamily: 'var(--font-b)', fontSize: 12, fontWeight: 300, color: 'var(--muted)' }}>
              Counts reflect unique people rostered to blocks on each day. Updates automatically as rosters change.
            </div>
          </div>
        </>
      )}
    </div>
  );
}
